import { cn } from '@/lib/cn';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from './Button';

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Área fixa no rodapé (ex.: Cancelar / Salvar) — fica sempre visível. */
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-manejai-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className={cn(
          'relative flex w-full max-h-[min(90vh,880px)] flex-col overflow-hidden rounded-2xl bg-white shadow-elevated',
          widths[size],
        )}
        role="dialog"
        aria-modal
      >
        <div className="flex shrink-0 items-center justify-between border-b border-manejai-sand px-5 py-4">
          <h2 className="font-display text-lg font-semibold text-manejai-forest">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Fechar">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer != null && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-manejai-sand bg-white px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
